/**
 * @fileoverview The four drawers that sit on top of the student tracker, driven by
 * `useStudentTrackerDrawers`, all in the My tasks drawer pattern (Mantine shell): the deliverable drawer
 * and the final self-assessment drawer come from My tasks; the lesson self-assessment and the read-only
 * teacher assessment live in this module.
 */

import { StudentFinalAssessmentDrawer } from 'pages/StudentTasks/finalAssessmentDrawer'
import { StudentTrackerDeliverableDrawer } from '../deliverableDrawer'
import type { StudentTrackerDrawers } from '../hooks/useStudentTrackerDrawers'
import { StudentLessonSelfAssessmentDrawer } from '../selfAssessmentDrawer'
import { StudentTeacherAssessmentDrawer } from '../teacherAssessmentDrawer'

interface StudentTrackerOverlaysProps {
  drawers: StudentTrackerDrawers
  projectId: string
  projectName?: string
  /** `students[0].id` of the payload. */
  studentId: string
  /** Reload the tracker keeping the table on screen (after a drawer wrote something). */
  onChanged: () => void
}

export function StudentTrackerOverlays({
  drawers,
  projectId,
  projectName,
  studentId,
  onChanged,
}: StudentTrackerOverlaysProps) {
  // Uploads/links persist inside the deliverable drawer, so "closed" may mean "changed".
  const closeDeliverable = () => {
    drawers.closeDeliverable()
    onChanged()
  }
  const closeFinal = () => {
    drawers.closeFinal()
    onChanged()
  }

  return (
    <>
      <StudentTrackerDeliverableDrawer
        key={drawers.deliverable.openToken}
        opened={drawers.deliverable.opened}
        activityId={drawers.deliverable.activityId}
        onClose={closeDeliverable}
        onSubmitted={onChanged}
      />

      <StudentLessonSelfAssessmentDrawer
        opened={drawers.selfAssessment.opened}
        lessonId={drawers.selfAssessment.lessonId}
        onClose={drawers.closeSelfAssessment}
        onSaved={onChanged}
      />

      <StudentFinalAssessmentDrawer
        opened={drawers.finalOpened}
        identifierId={projectId}
        onClose={closeFinal}
        onSaved={onChanged}
      />

      <StudentTeacherAssessmentDrawer
        opened={drawers.teacherAssessment.opened}
        lessonId={drawers.teacherAssessment.lessonId}
        lessonTitle={drawers.teacherAssessment.lessonTitle}
        lessonDate={drawers.teacherAssessment.lessonDate}
        assessmentId={drawers.teacherAssessment.assessmentId}
        studentId={studentId}
        projectName={projectName}
        onClose={drawers.closeTeacherAssessment}
      />
    </>
  )
}
